import { Injectable } from "@angular/core";
import { AngularFireDatabase } from "angularfire2/database";
import { Observable } from "rxjs/Observable";

@Injectable()
export class StateService {
    constructor(private db: AngularFireDatabase) { }

    getStates(): any {
        return this.db.list(`/location/state`).valueChanges() || false;
    }

    getCountryStates(countryCode: string): Observable<any> {
        return this.db.object(`/location/state/${countryCode}`)
            .valueChanges();
    }

    delete(countryCode, stateId): boolean {
        try {
            this.db.list(`/location/state/${countryCode}/${stateId}`).remove();

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    getStateById(countryCode, stateId): Observable<{}> {
        return this.db.object(`/location/state/${countryCode}/${stateId}`).valueChanges();
    }

    async import(data, countryCode): Promise<any> {
        try {
            const keys = data.shift();
            const existingStateNames = await this.getCountryNames(countryCode);
            const states = await this.constructData(data, keys);

            for (const state of states) {
                if (!existingStateNames.length) {
                    await this.store(state);
                    existingStateNames.push(state["stateName"]);
                }
                if (existingStateNames.length && !existingStateNames.includes(state["stateName"])) {
                    await this.store(state);
                    existingStateNames.push(state["stateName"]);
                }
            }

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    private async constructData(data, keys): Promise<Array<string>> {
        const states = [];
        data.forEach(values => {
            const state = {};
            keys.forEach((key, index) => {
                state[key] = values[index] ? values[index].trim() : "";
            });
            states.push(state);
        });

        return states;
    }

    private async store(state: any): Promise<any> {
        this.db
            .list(`/location/state/${state["countryCode"]}`)
            .push(state["stateName"]);
    }

    private async getCountryNames(countryCode: string): Promise<Array<string>> {
        return await new Promise<any>(resolve => {
            this.getCountryStates(countryCode).subscribe(
                existingStates => {
                    if (existingStates) {
                        resolve(Object.values(existingStates));
                    } else {
                        resolve([]);
                    }
                }
            );
        });
    }

    update(countryCode, state): boolean {
        try {
            const data = {};
            data[state["id"]] = state["name"];
            this.db.list(`/location/state`)
                .update(countryCode, data);

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    addStates(countryCode: string, states: Array<any>, state): boolean {
        try {
            if (state) {
                this.update(countryCode, state);
            } else {
                for (const newState of states) {
                    const data = {};
                    data["stateName"] = newState["name"];
                    data["countryCode"] = countryCode;
                    this.store(data);
                }
            }

            return true;
        } catch (e) {
            console.log(e);

            return false;
        }
    }

    initNgStates(): void {
        const promise = new Promise<Array<any>>(resolve => {
            this.db.list(`location/country/NG`)
            .valueChanges()
            .subscribe(ngCountry => {
                if (ngCountry && ngCountry.length) {
                    this.db.list(`location/state/NG`)
                        .valueChanges()
                        .subscribe(ngStates => {
                            resolve(ngStates);
                        });
                }
            });
        });

        promise.then(ngStates => {
            if (ngStates.length === 0) {
                const rawNgState = this.getNgStates();
                for (const state of rawNgState["NG"]) {
                    this.db.list(`location/state/NG`).push(state["name"]);
                }
            }
        }).catch(err => console.log("error ", err));
    }

    getIdsOfStatesInUse(): Observable<Array<string>> {
        const communities = this.db.list(`/communities`)
            .valueChanges();

        return communities.map(community => {
            return community.map(country => {
                const addressSnap = country["location"]["address"];

                if (addressSnap) {
                    return addressSnap["stateId"];
                }
            });
        });
    }

    getNgStates(): any {
        return {
            NG: [
                {
                    code: 0,
                    name: "Abia"
                },
                {
                    code: 1,
                    name: "Abuja Federal Capital Territory"
                },
                {
                    code: 2,
                    name: "Adamawa"
                },
                {
                    code: 3,
                    name: "Akwa Ibom"
                },
                {
                    code: 4,
                    name: "Anambra"
                },
                {
                    code: 5,
                    name: "Bauchi"
                },
                {
                    code: 6,
                    name: "Bayelsa"
                },
                {
                    code: 7,
                    name: "Benue"
                },
                {
                    code: 8,
                    name: "Borno"
                },
                {
                    code: 9,
                    name: "Cross River"
                },
                {
                    code: 10,
                    name: "Delta"
                },
                {
                    code: 11,
                    name: "Ebonyi"
                },
                {
                    code: 12,
                    name: "Edo"
                },
                {
                    code: 13,
                    name: "Ekiti"
                },
                {
                    code: 14,
                    name: "Enugu"
                },
                {
                    code: 15,
                    name: "Gombe"
                },
                {
                    code: 16,
                    name: "Imo"
                },
                {
                    code: 17,
                    name: "Jigawa"
                },
                {
                    code: 18,
                    name: "Kaduna"
                },
                {
                    code: 19,
                    name: "Kano"
                },
                {
                    code: 20,
                    name: "Katsina"
                },
                {
                    code: 21,
                    name: "Kebbi"
                },
                {
                    code: 22,
                    name: "Kogi"
                },
                {
                    code: 23,
                    name: "Kwara"
                },
                {
                    code: 24,
                    name: "Lagos"
                },
                {
                    code: 25,
                    name: "Nassarawa"
                },
                {
                    code: 26,
                    name: "Niger"
                },
                {
                    code: 27,
                    name: "Ogun"
                },
                {
                    code: 28,
                    name: "Ondo"
                },
                {
                    code: 29,
                    name: "Osun"
                },
                {
                    code: 30,
                    name: "Oyo"
                },
                {
                    code: 31,
                    name: "Plateau"
                },
                {
                    code: 32,
                    name: "Rivers"
                },
                {
                    code: 33,
                    name: "Sokoto"
                },
                {
                    code: 34,
                    name: "Taraba"
                },
                {
                    code: 35,
                    name: "Yobe"
                },
                {
                    code: 36,
                    name: "Zamfara"
                }
            ]
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/location/state.service.ts